import React, {FormEvent, useMemo, useState} from 'react';
import {getRandomDashboardHeroImage} from "../../helpers/dashboardHeroImages";
import {redeemAlphaInvite} from "../../api/alpha";
import './alpha-gate.scss';

export type AlphaGateProps = {
    onAuthorized: () => void;
};

export default function AlphaGate({onAuthorized}: AlphaGateProps) {
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitInviteCode = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const result = await redeemAlphaInvite(code);
            if (result.authorized) {
                onAuthorized();
                return;
            }

            setError('Invalid or unavailable invitation code.');
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Invalid or unavailable invitation code.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="alpha-gate" aria-labelledby="alpha-gate-title">
            <div className="alpha-gate__content">
                <div className="alpha-gate__kicker">Alpha Preview</div>
                <h1 id="alpha-gate-title" className="alpha-gate__title">BirdMap Explorer is invite-only right now.</h1>
                <p className="alpha-gate__copy">
                    This preview is limited while the app is still being tested. Enter your invitation code to continue.
                </p>
                <form className="alpha-gate__form" onSubmit={submitInviteCode}>
                    <label className="alpha-gate__label" htmlFor="alpha-invite-code">Invitation code</label>
                    <div className="alpha-gate__field-row">
                        <input
                            id="alpha-invite-code"
                            className="alpha-gate__input"
                            type="text"
                            value={code}
                            onChange={(event) => setCode(event.target.value)}
                            autoComplete="one-time-code"
                            spellCheck={false}
                            disabled={isSubmitting}
                            required
                        />
                        <button className="btn btn-primary alpha-gate__submit" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Checking' : 'Continue'}
                        </button>
                    </div>
                    {error ? <div className="alpha-gate__error" role="alert">{error}</div> : null}
                </form>
            </div>
            <aside
                className="alpha-gate__hero"
                aria-label="Bird in a natural habitat"
                style={{'--alpha-gate-hero-image': `url("${heroImage}")`} as React.CSSProperties}
            />
        </section>
    );
}
